import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { MOBILE_APP_VERSION } from '@/mobile/constants/mobile';
import { MobileApiService } from '@/services/mobileApiService';

export type AppVersionInfo = {
  appVersion: string;
  build?: string;
  platform: string;
  osVersion?: string;
  model?: string;
};

export type UpdateCheckResult = {
  updateAvailable: boolean;
  forceUpdate: boolean;
  currentVersion: string;
  latestVersion?: string;
  minVersion?: string;
  storeUrl?: string;
  message?: string;
  releaseNotes?: string;
};

export async function getDeviceAppInfo(): Promise<AppVersionInfo> {
  const appVersion = MOBILE_APP_VERSION;
  if (!Capacitor.isNativePlatform()) {
    return {
      appVersion,
      platform: 'web',
    };
  }
  try {
    const info = await Device.getInfo();
    return {
      appVersion,
      platform: info.platform,
      osVersion: info.osVersion,
      model: info.model,
    };
  } catch {
    return { appVersion, platform: Capacitor.getPlatform() };
  }
}

export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da > db) return 1;
    if (da < db) return -1;
  }
  return 0;
}

export async function checkForAppUpdate(): Promise<UpdateCheckResult> {
  const currentVersion = MOBILE_APP_VERSION;
  const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

  try {
    const res = await MobileApiService.getAppVersion(platform);
    const info = res.data;
    if (!info?.latestVersion) {
      return { updateAvailable: false, forceUpdate: false, currentVersion };
    }

    const belowMin = compareSemver(info.minVersion, currentVersion) > 0;
    const updateAvailable =
      belowMin || compareSemver(info.latestVersion, currentVersion) > 0;

    return {
      updateAvailable,
      forceUpdate: Boolean(info.forceUpdate || belowMin),
      currentVersion,
      latestVersion: info.latestVersion,
      minVersion: info.minVersion,
      storeUrl: info.storeUrl,
      releaseNotes: info.releaseNotes,
      message: info.releaseNotes,
    };
  } catch {
    return { updateAvailable: false, forceUpdate: false, currentVersion };
  }
}
